import React, { createContext, useCallback, useContext, useState } from 'react'
import {
  clearStoredConsent,
  getStoredConsent,
  setStoredConsent,
  type ConsentStatus,
} from '../analytics/consent'

export interface CookieConsentContextValue {
  status: ConsentStatus
  accept: () => void
  decline: () => void
  /** Withdraws a previous choice and re-opens the banner. */
  reset: () => void
}

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null)

/**
 * Top-level state for the cookie-consent gate. Mounted once in
 * AppWithConfig.tsx, above both the admin routes and the public
 * guest-facing routes, so a guest who never logs in still sees the banner
 * and is still asked before anything tracks them.
 *
 * `status` starts from localStorage (see analytics/consent.ts) so a
 * returning visitor who already answered is not asked again — but the
 * in-memory React state is what actually drives the banner and the
 * analytics gate, so `reset()` (withdrawing consent) re-shows the banner
 * immediately in the same session, not only after a reload.
 */
export function CookieConsentProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<ConsentStatus>(() => getStoredConsent())

  const accept = useCallback(() => {
    setStoredConsent('accepted')
    setStatus('accepted')
  }, [])

  const decline = useCallback(() => {
    setStoredConsent('declined')
    setStatus('declined')
  }, [])

  const reset = useCallback(() => {
    clearStoredConsent()
    setStatus('unanswered')
  }, [])

  return (
    <CookieConsentContext.Provider value={{ status, accept, decline, reset }}>
      {children}
    </CookieConsentContext.Provider>
  )
}

export function useCookieConsent(): CookieConsentContextValue {
  const context = useContext(CookieConsentContext)
  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider')
  }
  return context
}
